import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ApplyButton, ItemHeading, WideWhiteArea} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import styled from 'styled-components'
import {HAVE_PET_END_REASON, PET_TYPE} from '../../../../../CodeMaster'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import PPRadioGroupForResident from '../../../../../components/shared/forms/resident/PPRadioGroupForResident'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {HKConstants} from '../../../../../HKConstants'
import {NatPutPetBreedingStopRequest} from '../../repository/NatPutPetBreedingStop'
import {NatResidentPetBreedingStopLocationState} from '../NatResidentApplicationInterface'
import {PPTextAreaForResident} from '../../../../../components/shared/forms/resident/PPTextAreaForResident'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentPetBreedingStopLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    petType: Nullable<string>
    petBreed: Nullable<string>
    havePetEndDate: Nullable<string>
    havePetEndReason: Nullable<string>
    otherReason: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutPetBreedingStopRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    petType: string
    petBreed: string
    havePetEndDate: string
    havePetEndReason: string
    otherReason: Nullable<string>

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatResidentPetBreedingStopForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applicantFamilyName: '',
            applicantFirstName: '',
            petType: '',
            petBreed: '',
            havePetEndDate: '',
            havePetEndReason: '',
            otherReason: null,
            currentDate: moment(),
            holidays: [],
            isLoadCompleted: false
        }

        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangePetType = this.handleChangePetType.bind(this)
        this.handleChangeHavePetEndDate = this.handleChangeHavePetEndDate.bind(this)
        this.handleHavePetEndReason = this.handleHavePetEndReason.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const petType = this.props.petType ?? this.state.petType
            const petBreed = this.props.petBreed ?? this.state.petBreed
            const havePetEndDate = this.props.havePetEndDate ?? this.state.havePetEndDate
            const havePetEndReason = this.props.havePetEndReason ?? this.state.havePetEndReason
            const otherReason = this.props.otherReason ?? this.state.otherReason
            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                petType: petType,
                petBreed: petBreed,
                havePetEndDate: havePetEndDate,
                havePetEndReason: havePetEndReason,
                otherReason: otherReason,
                holidays: holidays === null ? [] : holidays
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
        }
        this.props.eventEmitter.closeLoading()
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangePetType(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            petType: event.target.value,
        }, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleHavePetEndReason(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            havePetEndReason: event.target.value,
            otherReason: null
        }, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeHavePetEndDate(date: string): void {
        this.setState({havePetEndDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, petType, petBreed,
            havePetEndDate, havePetEndReason, otherReason
        } = this.state

        let applyButtonDisabled = false

        if (!applicantFamilyName || !applicantFirstName || !petType ||
            !petBreed || !havePetEndDate || !havePetEndReason) {
            applyButtonDisabled = true
        }

        if (havePetEndReason === HAVE_PET_END_REASON.OTHER.value) {
            if (!otherReason) {
                applyButtonDisabled = true
            }
        }

        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, petType, petBreed,
            havePetEndDate, havePetEndReason, otherReason
        } = this.state

        const request: NatPutPetBreedingStopRequest = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            petType: petType,
            petBreed: petBreed,
            havePetEndDate: havePetEndDate,
            havePetEndReason: havePetEndReason,
            otherReason: otherReason,
            confirmed: false
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER)
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: applicantFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderPetTypeForm(): ReactElement {
        const {petType} = this.state

        return (
            <>
                <ItemHeading>
                    種類
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>飼育を中止する<BreakPcOnly/>ペットの種類</span>}
                        required
                        component={
                            <FlexContainer>
                                <PPRadioGroupForResident
                                    data={PET_TYPE.values()}
                                    selectValue={petType}
                                    onChange={this.handleChangePetType}
                                    eventKeys={['petType']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderPetBreedForm(): ReactElement {
        const {petBreed} = this.state

        return (
            <>
                <ItemHeading>
                    品種
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>飼育を中止する<BreakPcOnly/>ペットの品種</span>}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        maxLength: 50,
                                        name: 'petBreed',
                                        value: petBreed,
                                        placeholder: '例：チワワ'
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['petBreed']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderHavePetEndDateForm(): ReactElement {
        const {havePetEndDate, holidays} = this.state

        return (
            <>
                <ItemHeading>
                    飼育中止日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'飼育を中止する日付'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={havePetEndDate}
                                onChange={this.handleChangeHavePetEndDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['havePetEndDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderHavePetEndReasonForm(): ReactElement {
        const {havePetEndReason, otherReason} = this.state

        return (
            <>
                <ItemHeading>
                    飼育中止理由
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'飼育を中止する理由'}
                        required
                        component={
                            <GridContainer>
                                <GridItem over={12}>
                                    <PPRadioGroupForResident
                                        data={HAVE_PET_END_REASON.values()}
                                        selectValue={havePetEndReason}
                                        onChange={this.handleHavePetEndReason}
                                        eventKeys={['havePetEndReason']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                </GridItem>
                            </GridContainer>
                        }
                    />
                    {
                        havePetEndReason === HAVE_PET_END_REASON.OTHER.value &&
                        <>
                            <Box height={'20px'}/>
                            <NatResidentApplicationDetailFormItem
                                title={'その他飼育中止理由'}
                                required
                                component={
                                    <FlexContainer>
                                        <PPTextAreaForResident
                                            inputProps={{
                                                maxLength: 50,
                                                name: 'otherReason',
                                                value: otherReason,
                                                placeholder: '例：逃走したため（50文字以内）'
                                            }}
                                            onChange={this.handleChangeText}
                                            eventKeys={['otherReason']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </FlexContainer>
                                }
                            />
                        </>
                    }
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderPetTypeForm()}
                < Box height={'15px'}/>
                {this.renderPetBreedForm()}
                <Box height={'15px'}/>
                {this.renderHavePetEndDateForm()}
                <Box height={'15px'}/>
                {this.renderHavePetEndReasonForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
